import React, { useState } from "react";
import { useIntl } from "react-intl";

import { Panel } from "../../../resusableComponents/funnelFuel";

import { useFilters } from "../lib/FilterContext";
import { fmt } from "../lib/selectors";

const COLLAPSE_AT = 5;

/* Delivery by line item: one row per line item on the order, however many there are. */
export const LineItemsPanel = ({ view, model }) => {
  const intl = useIntl();
  const { toggle, isSelected, active } = useFilters();
  // Collapse state is local component state, NOT the filter store.
  const [expanded, setExpanded] = useState(false);

  const { rows, overallPercent } = view.lineItems;
  const collapsed = rows.length > COLLAPSE_AT && !expanded;
  const visible = collapsed ? rows.slice(0, COLLAPSE_AT) : rows;

  const t = (id, defaultMessage, values) => intl.formatMessage({ id: `ff_dashboard.line_items.${id}`, defaultMessage }, values);

  return (
    <Panel
      title={t("title", "Delivery by line item")}
      caption={t("caption", "One row per line item on the order, however many there are")}
      isEmpty={rows.length === 0}
      emptyMessage={intl.formatMessage({ id: "ff_dashboard.empty.line_items", defaultMessage: "There are no line items on this campaign." })}
      flush
    >
      <div className="ff-table-wrap">
        <table className="ff-table">
          <thead>
            <tr>
              <th scope="col">{t("col_name", "Line item")}</th>
              <th scope="col" className="ff-num">{t("col_booked", "Booked")}</th>
              <th scope="col" className="ff-num">{t("col_delivered", "Delivered")}</th>
              <th scope="col" style={{ width: "26%" }}>{t("col_progress", "Progress")}</th>
              <th scope="col" className="ff-num">%</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const pressed = isSelected(row.selection);

              return (
                <tr
                  key={row.id}
                  className="ff-clickable"
                  data-pressed={pressed || undefined}
                  data-muted={(active && !pressed && !row.selected) || undefined}
                  // Mouse convenience only; the button in the first cell is the keyboard/screen-reader control.
                  onClick={() => toggle(row.selection)}
                >
                  <td className="ff-truncate">
                    <button
                      type="button"
                      className="ff-row-btn"
                      aria-pressed={pressed}
                      title={row.name}
                      onClick={(event) => { event.stopPropagation(); toggle(row.selection); }}
                    >
                      {row.name}
                    </button>
                  </td>
                  {/* Booked NEVER changes under a filter: it is contracted at setup, not derived from the leads. */}
                  <td className="ff-num">{fmt(row.booked)}</td>
                  <td className="ff-num">
                    {active
                      ? <>{fmt(row.selected)} <span className="ff-dim-text">/ {fmt(row.delivered)}</span></>
                      : fmt(row.delivered)}
                  </td>
                  <td>
                    <div className="ff-bar-track" aria-hidden="true">
                      {/* The bar caps at 100%, but the true percent still shows as text. */}
                      <div className="ff-bar-fill" style={{ width: `${Math.min(100, row.percent)}%` }} />
                    </div>
                  </td>
                  <td className="ff-num" data-warn={row.behind || undefined}>
                    {row.percent}%
                    {row.behind && <span className="ff-sr-only"> {t("behind", "Behind the overall pace")}</span>}
                  </td>
                </tr>
              );
            })}

            {rows.length > COLLAPSE_AT && (
              <tr>
                <td colSpan={5} className="ff-more-cell">
                  <button type="button" className="ff-btn-quiet ff-btn-small" aria-expanded={!collapsed} onClick={() => setExpanded((e) => !e)}>
                    {collapsed
                      ? t("show_all", "Show all {count} line items", { count: rows.length })
                      : t("show_top", "Show top {count} only", { count: COLLAPSE_AT })}
                  </button>
                </td>
              </tr>
            )}

            {/* The total row always counts every line item, collapsed or not. */}
            <tr className="ff-total">
              <td>{t("total", "Campaign total")}</td>
              <td className="ff-num">{fmt(model.campaign.booked)}</td>
              <td className="ff-num">{active ? `${fmt(view.total)} / ${fmt(view.totalAll)}` : fmt(view.totalAll)}</td>
              <td>
                <div className="ff-bar-track" aria-hidden="true">
                  <div className="ff-bar-fill" style={{ width: `${Math.min(100, overallPercent)}%` }} />
                </div>
              </td>
              <td className="ff-num">{overallPercent}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Panel>
  );
};
